import { sectionIconMap } from './sectionIconMap';
import React from 'react';
import { __ } from 'react-i18n';
import { ChematCustomerType } from '../library/ChematCustomerType';
import { ChematCustomerTypeSalePrice } from '../library/ChematCustomerTypeSalePrice';
import {
  SIMPLE_TEMPLATE_URLS,
  URL_FOR_SCHOOLS,
  URL_LOGIN,
  URL_REGISTRATION_COMPANY,
  URL_REGISTRATION_COMPLETE_COMPANY,
  URL_REGISTRATION_COMPLETE_RETAIL,
  URL_REGISTRATION_COMPLETE_SCHOOL,
  URL_REGISTRATION_RETAIL,
  URL_REGISTRATION_SCHOOL,
  URL_SET,
  URL_SETS,
} from '../constants';
import dayjs from 'dayjs';
import { setGlobalMetaTags } from '../containers/App/actions';
import { store } from '../index';
import { push } from 'react-router-redux';
import * as cookie from 'react-cookies';
import 'dayjs/locale/sk';
import { CART_PROTECTION_COOKIE } from '../containers/App/constants';

export const getProp = (object, path, defaultValue: any = '') => {
  if (path === undefined || path === null || path.length <= 0) {
    return defaultValue;
  }
  if (typeof path !== 'string') {
    throw new Error(`Invalid getProp argument path of type: ${typeof path}`);
  }
  const val = path.split('.').reduce((o, x) => {
    return typeof o === 'undefined' || o === null ? defaultValue : o[x];
  }, object);
  if (typeof val === 'undefined' || val === null) {
    return defaultValue;
  }
  return val;
};

export const getIdFromProps = (props) => {
  let id = props.id;
  if (!id) {
    id = getProp(props, 'params.id', '');
  }
  return String(id);
};

export const getPictureForSection = (section: { uniqid: number }) => {
  return sectionIconMap[section.uniqid] || null;
};

export const prop = (object, path, defaultValue: any = null) => {
  const val = path.split('.').reduce((o, x) => {
    return typeof o === 'undefined' || o === null ? defaultValue : o[x];
  }, object);
  if (typeof val === 'undefined') {
    return defaultValue;
  }
  return val;
};

export const countDecimals = (i: number) => {
  if (Math.floor(i.valueOf()) === i.valueOf()) {
    return 0;
  }
  return i.toString().split('.')[1].length || 0;
};

export const propByKey = (obj, key, def) => {
  if (obj !== undefined && obj[key] !== undefined) {
    return obj[key];
  }

  return def;
};

export const getNumericQueryProp = (obj, key, def) => {
  if (obj !== undefined && obj[key] !== undefined) {
    if (!isNaN(obj[key])) {
      return obj[key];
    }
  }

  return def;
};

export function formatDate(
  date: Date,
  format: string = 'YYYY-MM-DD HH:mm:ss',
): string {
  return dayjs(date).format(format);
}

export function formatStringDate(
  date: string,
  format: string = 'DD.MM.YYYY',
): string {
  return dayjs(date).locale('sk').format(format);
}

export function formatPrice(
  price: number,
  currency: string,
  decimalPlaces: number = 2,
  zeroPriceText: boolean = false,
  stripZero: boolean = false,
) {
  let priceValue: string;

  if (Number.isInteger(price)) {
    priceValue = `${price},${'0'.repeat(decimalPlaces)}`;
  } else {
    priceValue = price.toFixed(decimalPlaces).replace('.', ',');
  }

  if (zeroPriceText && price === 0) {
    return __('ZADARMO');
  }

  if (stripZero) {
    priceValue = priceValue.replace(',00', '');
  }

  switch (currency) {
    case 'EUR':
      return (
        <>
          {priceValue}
          &nbsp;€
        </>
      );
    default:
      return priceValue;
  }
}

export const checkMetaTag = (tag) => {
  if (typeof tag === 'string') {
    return tag;
  } else {
    return '';
  }
};

export const cookiesExpiresDate = () => {
  // expires in 1 year
  return new Date(new Date().setFullYear(new Date().getFullYear() + 1));
};

export const cookiesCartExpiresDate = () => {
  // expires in 1 year
  return new Date(new Date().setFullYear(new Date().getFullYear() + 1));
};

export const transformRemainingItemsText = (count) => {
  if (count === 1) {
    return `...${__('ďalší')} ${count} ${__('produkt v košíku')}`;
  }

  if (count > 1 && count < 5) {
    return `...${__('ďalšie')} ${count} ${__('produkty v košíku')}`;
  }
  if (count >= 5) {
    return `...${__('ďalšých')} ${count} ${__('produktov v košíku')}`;
  }

  return null;
};

export const getBaseUrl = (url: string) => {
  if (url && !url.startsWith('/') && !url.startsWith('http')) {
    return '/' + url;
  }

  return url;
};

export const categoryMetaHandler = (data, lang, defaultTitle) => {
  const content = prop(data, 'content', null);
  const meta = prop(
    content && content.length ? content[0] : content,
    'json_content.meta',
    null,
  );

  const title = data.category_name
    ? data.category_name
    : data.name
    ? data.name
    : '';

  return {
    title: prop(meta, 'title', `${defaultTitle} - ${title}`),
    description: prop(meta, 'description', null),
    keywords: prop(meta, 'keywords', null),
    og_article_author: prop(meta, 'og_article_author', null),
    og_article_section: prop(meta, 'og_article_section', null),
    og_article_tag: prop(meta, 'og_article_tag', null),
    og_description: prop(meta, 'og_description', null),
    og_image: prop(meta, 'og_image', null),
    og_title: prop(meta, 'og_title', null),
    og_type: prop(meta, 'og_type', 'website'),
    og_url: prop(meta, 'og_url', null),
  };
};

export const brandMetaHandler = (data, lang, defaultTitle) => {
  const translation = data.translations.find((t) => t.lang_id == lang);

  if (translation) {
    return {
      title: prop(
        translation,
        'meta_title',
        `${defaultTitle} - ${data.brand_name}`,
      ),
      description: prop(translation, 'meta_description', null),
      keywords: prop(translation, 'meta_keywords', null),
      og_description: prop(translation, 'opengraph_description', null),
      og_image: prop(translation, 'opengraph_picture', null),
      og_title: prop(translation, 'opengraph_title', null),
      og_type: prop(translation, 'opengraph_type', 'website'),
      og_url: prop(translation, 'opengraph_url', null),
    };
  } else {
    return {
      title: prop(data, 'meta_title', `${defaultTitle} - ${data.brand_name}`),
    };
  }
};

export enum cartRoutes {
  FIRST_STEP = 'routes:kosik',
  SECOND_STEP = 'routes:kosik/doprava-a-platba',
  THIRD_STEP = 'routes:kosik/informacie-o-vas',
}

export const isSsr = () =>
  typeof window === 'undefined' || !window.addEventListener;

export function slugify(text) {
  const a = 'àáäâãåăæçèéëêǵḧìíïîḿńǹñòóöôœṕŕßśșțùúüûǘẃẍÿź·/_,:;šťžŽľĺýŕôňčď';
  const b = 'aaaaaaaaceeeeghiiiimnnnoooooprssstuuuuuwxyz------stzZllyroncd';
  const p = new RegExp(a.split('').join('|'), 'g');
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(p, (c) => b.charAt(a.indexOf(c))) // Replace special characters
    .replace(/&/g, '-and-') // Replace & with ‘and’
    .replace(/[^\w\-]+/g, '') // Remove all non-word characters
    .replace(/\-\-+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
}

export function isEmailValid(email) {
  if (!email) {
    return false;
  }

  const regexValid = /^[a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-.]{0,61}[a-zA-Z0-9])?(?:\.(id|aaa|aarp|abarth|abb|abbott|abbvie|abc|able|abogado|abudhabi|ac|academy|accenture|accountant|accountants|aco|active|actor|ad|adac|ads|adult|ae|aeg|aero|aetna|af|afamilycompany|afl|africa|ag|agakhan|agency|ai|aig|aigo|airbus|airforce|airtel|akdn|al|alfaromeo|alibaba|alipay|allfinanz|allstate|ally|alsace|alstom|am|americanexpress|americanfamily|amex|amfam|amica|amsterdam|analytics|android|anquan|anz|ao|aol|apartments|app|apple|aq|aquarelle|ar|arab|aramco|archi|army|arpa|art|arte|as|asda|asia|associates|at|athleta|attorney|au|auction|audi|audible|audio|auspost|author|auto|autos|avianca|aw|aws|ax|axa|az|azure|ba|baby|baidu|banamex|bananarepublic|band|bank|bar|barcelona|barclaycard|barclays|barefoot|bargains|baseball|basketball|bauhaus|bayern|bb|bbc|bbt|bbva|bcg|bcn|bd|be|beats|beauty|beer|bentley|berlin|best|bestbuy|bet|bf|bg|bh|bharti|bi|bible|bid|bike|bing|bingo|bio|biz|bj|black|blackfriday|blanco|blockbuster|blog|bloomberg|blue|bm|bms|bmw|bn|bnl|bnpparibas|bo|boats|boehringer|bofa|bom|bond|boo|book|booking|bosch|bostik|boston|bot|boutique|box|br|bradesco|bridgestone|broadway|broker|brother|brussels|bs|bt|budapest|bugatti|build|builders|business|buy|buzz|bv|bw|by|bz|bzh|ca|cab|cafe|cal|call|calvinklein|cam|camera|camp|cancerresearch|canon|capetown|capital|capitalone|car|caravan|cards|care|career|careers|cars|cartier|casa|case|caseih|cash|casino|cat|catering|catholic|cba|cbn|cbre|cbs|cc|cd|ceb|center|ceo|cern|cf|cfa|cfd|cg|ch|chanel|channel|chase|chat|cheap|chintai|christmas|chrome|chrysler|church|ci|cipriani|circle|cisco|citadel|citi|citic|city|cityeats|ck|cl|claims|cleaning|click|clinic|clinique|clothing|cloud|club|clubmed|cm|cn|co|coach|codes|coffee|college|cologne|com|comcast|commbank|community|company|compare|computer|comsec|condos|construction|consulting|contact|contractors|cooking|cookingchannel|cool|coop|corsica|country|coupon|coupons|courses|cr|credit|creditcard|creditunion|cricket|crown|crs|cruise|cruises|csc|cu|cuisinella|cv|cw|cx|cy|cymru|cyou|cz|dabur|dad|dance|data|date|dating|datsun|day|dclk|dds|de|deal|dealer|deals|degree|delivery|dell|deloitte|delta|democrat|dental|dentist|desi|design|dev|dhl|diamonds|diet|digital|direct|directory|discount|discover|dish|diy|dj|dk|dm|dnp|do|docs|doctor|dodge|dog|doha|domains|dot|download|drive|dtv|dubai|duck|dunlop|duns|dupont|durban|dvag|dvr|dz|earth|eat|ec|eco|edeka|edu|education|ee|eg|email|emerck|energy|engineer|engineering|enterprises|epost|epson|equipment|er|ericsson|erni|es|esq|estate|esurance|et|etisalat|eu|eurovision|eus|events|everbank|exchange|expert|exposed|express|extraspace|fage|fail|fairwinds|faith|family|fan|fans|farm|farmers|fashion|fast|fedex|feedback|ferrari|ferrero|fi|fiat|fidelity|fido|film|final|finance|financial|fire|firestone|firmdale|fish|fishing|fit|fitness|fj|fk|flickr|flights|flir|florist|flowers|fly|fm|fo|foo|food|foodnetwork|football|ford|forex|forsale|forum|foundation|fox|fr|free|fresenius|frl|frogans|frontdoor|frontier|ftr|fujitsu|fujixerox|fun|fund|furniture|futbol|fyi|ga|gal|gallery|gallo|gallup|game|games|gap|garden|gb|gbiz|gd|gdn|ge|gea|gent|genting|george|gf|gg|ggee|gh|gi|gift|gifts|gives|giving|gl|glade|glass|gle|global|globo|gm|gmail|gmbh|gmo|gmx|gn|godaddy|gold|goldpoint|golf|goo|goodhands|goodyear|goog|google|gop|got|gov|gp|gq|gr|grainger|graphics|gratis|green|gripe|grocery|group|gs|gt|gu|guardian|gucci|guge|guide|guitars|guru|gw|gy|hair|hamburg|hangout|haus|hbo|hdfc|hdfcbank|health|healthcare|help|helsinki|here|hermes|hgtv|hiphop|hisamitsu|hitachi|hiv|hk|hkt|hm|hn|hockey|holdings|holiday|homedepot|homegoods|homes|homesense|honda|honeywell|horse|hospital|host|hosting|hot|hoteles|hotels|hotmail|house|how|hr|hsbc|ht|hu|hughes|hyatt|hyundai|ibm|icbc|ice|icu|id|ie|ieee|ifm|ikano|il|im|imamat|imdb|immo|immobilien|in|industries|infiniti|info|ing|ink|institute|insurance|insure|int|intel|international|intuit|investments|io|ipiranga|iq|ir|irish|is|iselect|ismaili|ist|istanbul|it|itau|itv|iveco|iwc|jaguar|java|jcb|jcp|je|jeep|jetzt|jewelry|jio|jlc|jll|jm|jmp|jnj|jo|jobs|joburg|jot|joy|jp|jpmorgan|jprs|juegos|juniper|kaufen|kddi|ke|kerryhotels|kerrylogistics|kerryproperties|kfh|kg|kh|ki|kia|kim|kinder|kindle|kitchen|kiwi|km|kn|koeln|komatsu|kosher|kp|kpmg|kpn|kr|krd|kred|kuokgroup|kw|ky|kyoto|kz|la|lacaixa|ladbrokes|lamborghini|lamer|lancaster|lancia|lancome|land|landrover|lanxess|lasalle|lat|latino|latrobe|law|lawyer|lb|lc|lds|lease|leclerc|lefrak|legal|lego|lexus|lgbt|li|liaison|lidl|life|lifeinsurance|lifestyle|lighting|like|lilly|limited|limo|lincoln|linde|link|lipsy|live|living|lixil|lk|llc|loan|loans|locker|locus|loft|lol|london|lotte|lotto|love|lpl|lplfinancial|lr|ls|lt|ltd|ltda|lu|lundbeck|lupin|luxe|luxury|lv|ly|ma|macys|madrid|maif|maison|makeup|man|management|mango|map|market|marketing|markets|marriott|marshalls|maserati|mattel|mba|mc|mckinsey|md|me|med|media|meet|melbourne|meme|memorial|men|menu|meo|merckmsd|metlife|mg|mh|miami|microsoft|mil|mini|mint|mit|mitsubishi|mk|ml|mlb|mls|mm|mma|mn|mo|mobi|mobile|mobily|moda|moe|moi|mom|monash|money|monster|mopar|mormon|mortgage|moscow|moto|motorcycles|mov|movie|movistar|mp|mq|mr|ms|msd|mt|mtn|mtr|mu|museum|mutual|mv|mw|mx|my|mz|na|nab|nadex|nagoya|name|nationwide|natura|navy|nba|nc|ne|nec|net|netbank|netflix|network|neustar|new|newholland|news|next|nextdirect|nexus|nf|nfl|ng|ngo|nhk|ni|nico|nike|nikon|ninja|nissan|nissay|nl|no|nokia|northwesternmutual|norton|now|nowruz|nowtv|np|nr|nra|nrw|ntt|nu|nyc|nz|obi|observer|off|office|okinawa|olayan|olayangroup|oldnavy|ollo|om|omega|one|ong|onl|online|onyourside|ooo|open|oracle|orange|org|organic|origins|osaka|otsuka|ott|ovh|pa|page|panasonic|panerai|paris|pars|partners|parts|party|passagens|pay|pccw|pe|pet|pf|pfizer|pg|ph|pharmacy|phd|philips|phone|photo|photography|photos|physio|piaget|pics|pictet|pictures|pid|pin|ping|pink|pioneer|pizza|pk|pl|place|play|playstation|plumbing|plus|pm|pn|pnc|pohl|poker|politie|porn|post|pr|pramerica|praxi|press|prime|pro|prod|productions|prof|progressive|promo|properties|property|protection|pru|prudential|ps|pt|pub|pw|pwc|py|qa|qpon|quebec|quest|qvc|racing|radio|raid|re|read|realestate|realtor|realty|recipes|red|redstone|redumbrella|rehab|reise|reisen|reit|reliance|ren|rent|rentals|repair|report|republican|rest|restaurant|review|reviews|rexroth|rich|richardli|ricoh|rightathome|ril|rio|rip|rmit|ro|rocher|rocks|rodeo|rogers|room|rs|rsvp|ru|rugby|ruhr|run|rw|rwe|ryukyu|sa|saarland|safe|safety|sakura|sale|salon|samsclub|samsung|sandvik|sandvikcoromant|sanofi|sap|sapo|sarl|sas|save|saxo|sb|sbi|sbs|sc|sca|scb|schaeffler|schmidt|scholarships|school|schule|schwarz|science|scjohnson|scor|scot|sd|se|search|seat|secure|security|seek|select|sener|services|ses|seven|sew|sex|sexy|sfr|sg|sh|shangrila|sharp|shaw|shell|shia|shiksha|shoes|shop|shopping|shouji|show|showtime|shriram|si|silk|sina|singles|site|sj|sk|ski|skin|sky|skype|sl|sling|sm|smart|smile|sn|sncf|so|soccer|social|softbank|software|sohu|solar|solutions|song|sony|soy|space|spiegel|sport|spot|spreadbetting|sr|srl|srt|st|stada|staples|star|starhub|statebank|statefarm|statoil|stc|stcgroup|stockholm|storage|store|stream|studio|study|style|su|sucks|supplies|supply|support|surf|surgery|suzuki|sv|swatch|swiftcover|swiss|sx|sy|sydney|symantec|systems|sz|tab|taipei|talk|taobao|target|tatamotors|tatar|tattoo|tax|taxi|tc|tci|td|tdk|team|tech|technology|tel|telecity|telefonica|temasek|tennis|teva|tf|tg|th|thd|theater|theatre|tiaa|tickets|tienda|tiffany|tips|tires|tirol|tj|tjmaxx|tjx|tk|tkmaxx|tl|tm|tmall|tn|to|today|tokyo|tools|top|toray|toshiba|total|tours|town|toyota|toys|tr|trade|trading|training|travel|travelchannel|travelers|travelersinsurance|trust|trv|tt|tube|tui|tunes|tushu|tv|tvs|tw|tz|ua|ubank|ubs|uconnect|ug|uk|unicom|university|uno|uol|ups|us|uy|uz|va|vacations|vana|vanguard|vc|ve|vegas|ventures|verisign|versicherung|vet|vg|vi|viajes|video|vig|viking|villas|vin|vip|virgin|visa|vision|vista|vistaprint|viva|vivo|vlaanderen|vn|vodka|volkswagen|volvo|vote|voting|voto|voyage|vu|vuelos|wales|walmart|walter|wang|wanggou|warman|watch|watches|weather|weatherchannel|webcam|weber|website|wed|wedding|weibo|weir|wf|whoswho|wien|wiki|williamhill|win|windows|wine|winners|wme|wolterskluwer|woodside|work|works|world|wow|ws|wtc|wtf|xbox|xerox|xfinity|xihuan|xin|xn--11b4c3d|xn--1ck2e1b|xn--1qqw23a|xn--2scrj9c|xn--30rr7y|xn--3bst00m|xn--3ds443g|xn--3e0b707e|xn--3hcrj9c|xn--3oq18vl8pn36a|xn--3pxu8k|xn--42c2d9a|xn--45br5cyl|xn--45brj9c|xn--45q11c|xn--4gbrim|xn--54b7fta0cc|xn--55qw42g|xn--55qx5d|xn--5su34j936bgsg|xn--5tzm5g|xn--6frz82g|xn--6qq986b3xl|xn--80adxhks|xn--80ao21a|xn--80aqecdr1a|xn--80asehdb|xn--80aswg|xn--8y0a063a|xn--90a3ac|xn--90ae|xn--90ais|xn--9dbq2a|xn--9et52u|xn--9krt00a|xn--b4w605ferd|xn--bck1b9a5dre4c|xn--c1avg|xn--c2br7g|xn--cck2b3b|xn--cg4bki|xn--clchc0ea0b2g2a9gcd|xn--czr694b|xn--czrs0t|xn--czru2d|xn--d1acj3b|xn--d1alf|xn--e1a4c|xn--eckvdtc9d|xn--efvy88h|xn--estv75g|xn--fct429k|xn--fhbei|xn--fiq228c5hs|xn--fiq64b|xn--fiqs8s|xn--fiqz9s|xn--fjq720a|xn--flw351e|xn--fpcrj9c3d|xn--fzc2c9e2c|xn--fzys8d69uvgm|xn--g2xx48c|xn--gckr3f0f|xn--gecrj9c|xn--gk3at1e|xn--h2breg3eve|xn--h2brj9c|xn--h2brj9c8c|xn--hxt814e|xn--i1b6b1a6a2e|xn--imr513n|xn--io0a7i|xn--j1aef|xn--j1amh|xn--j6w193g|xn--jlq61u9w7b|xn--jvr189m|xn--kcrx77d1x4a|xn--kprw13d|xn--kpry57d|xn--kpu716f|xn--kput3i|xn--l1acc|xn--lgbbat1ad8j|xn--mgb9awbf|xn--mgba3a3ejt|xn--mgba3a4f16a|xn--mgba7c0bbn0a|xn--mgbaakc7dvf|xn--mgbaam7a8h|xn--mgbab2bd|xn--mgbai9azgqp6j|xn--mgbayh7gpa|xn--mgbb9fbpob|xn--mgbbh1a|xn--mgbbh1a71e|xn--mgbc0a9azcg|xn--mgbca7dzdo|xn--mgberp4a5d4ar|xn--mgbgu82a|xn--mgbi4ecexp|xn--mgbpl2fh|xn--mgbt3dhd|xn--mgbtx2b|xn--mgbx4cd0ab|xn--mix891f|xn--mk1bu44c|xn--mxtq1m|xn--ngbc5azd|xn--ngbe9e0a|xn--ngbrx|xn--node|xn--nqv7f|xn--nqv7fs00ema|xn--nyqy26a|xn--o3cw4h|xn--ogbpf8fl|xn--otu796d|xn--p1acf|xn--p1ai|xn--pbt977c|xn--pgbs0dh|xn--pssy2u|xn--q9jyb4c|xn--qcka1pmc|xn--qxam|xn--rhqv96g|xn--rovu88b|xn--rvc1e0am3e|xn--s9brj9c|xn--ses554g|xn--t60b56a|xn--tckwe|xn--tiq49xqyj|xn--unup4y|xn--vermgensberater-ctb|xn--vermgensberatung-pwb|xn--vhquv|xn--vuq861b|xn--w4r85el8fhu5dnra|xn--w4rs40l|xn--wgbh1c|xn--wgbl6a|xn--xhq521b|xn--xkc2al3hye2a|xn--xkc2dl3a5ee0h|xn--y9a3aq|xn--yfro4i67o|xn--ygbi2ammx|xn--zfr164b|xperia|xxx|xyz|yachts|yahoo|yamaxun|yandex|ye|yodobashi|yoga|yokohama|you|youtube|yt|yun|za|zappos|zara|zero|zip|zippo|zm|zone|zuerich|zw))$/.test(
    email.trim(),
  );

  // kontrola nevalidnej domeny
  const emailDomain = email?.split('@')[1];
  const invalidDomains = store?.getState()?.general.invalidEmails || [];
  const domainValid = emailDomain && !invalidDomains.includes(emailDomain);

  return regexValid && domainValid;
}

export function isPriceRangeRetail(user) {
  if (!user) {
    return true;
  }
  if (
    user.skvelko_type &&
    (user.skvelko_type === ChematCustomerType.RETAIL ||
      (!user.type_sale_price &&
        user.skvelko_type === ChematCustomerType.COMPANY))
  ) {
    return true;
  }
  return !!(
    user.type_sale_price &&
    user.type_sale_price === ChematCustomerTypeSalePrice.PC3
  );
}

export function isCartCointainsSkvelkoSet(cart) {
  return cart && cart.items && cart.items.some((item) => !!item.skvelko_set);
}

export function isCartCointainsUniversalSkvelkoSet(cart) {
  return (
    cart &&
    cart.items &&
    cart.items.some(
      (item) =>
        item.skvelko_set &&
        (item.skvelko_set.clubUserId == 11602 ||
          item.skvelko_set.clubUserId == 11609),
    )
  );
}

export function getCartSkvelkoSetSchoolIds(cart): Array<number> {
  if (cart && cart.items) {
    return cart.items
      .filter((item) => !!item.skvelko_set)
      .map((item) => item.skvelko_set.clubUserId);
  } else {
    return [];
  }
}

function findPos(obj) {
  let curtop = 0;
  if (obj.offsetParent) {
    do {
      curtop += obj.offsetTop;
      obj = obj.offsetParent;
    } while (obj);
    return curtop;
  }

  return curtop;
}

export function scrollToElement(className, offsetSize = 0, timeout = 0) {
  if (isSsr() || typeof window.scrollTo !== 'function') {
    return;
  }
  setTimeout(() => {
    const errorElements = document.getElementsByClassName(className);
    if (errorElements.length > 0) {
      const errorElement = errorElements[0];
      const position = findPos(errorElement);
      try {
        window.scrollTo({
          top: position - offsetSize,
          left: 0,
          behavior: 'smooth',
        });
      } catch (e) {}
    }
  }, timeout);
}

export function isTrustpay(paymentType) {
  return paymentType === 'TRUSTPAY';
}

export function isTrustCard(paymentType) {
  return paymentType === 'TRUSTCARD';
}

export function isDobierka(paymentType) {
  return paymentType === 'DOBIERKA';
}

export function isGpWebPay(paymentType) {
  return paymentType === 'GPWEBPAY';
}

export function isVyzvaNaPrevod(paymentType) {
  return paymentType === 'VyzvaNaPrevod';
}

export function isFaktura(paymentType) {
  return paymentType === 'FAKTURA';
}

export function isZbernaFaktura(paymentType) {
  return paymentType === 'ZBERNA_FAKTURA';
}

export function isDeliveryToSchool(paymentType) {
  return paymentType === 'DOPRAVA_DO_SKOLY';
}

export function isDpdDelivery(deliveryType) {
  return deliveryType === 'DPD_PICKUP';
}

export function isPacketaDelivery(deliveryType) {
  return deliveryType === 'PACKETA';
}

export function isShopPersonalPickup(deliveryType) {
  return deliveryType === 'PREDAJNA';
}

export function isTablet() {
  return !isSsr() && window && window.innerWidth <= 992;
}

export function isMobile() {
  return !isSsr() && window && window.innerWidth <= 767;
}

export function isNotMobile() {
  return !isSsr() && window && window.innerWidth > 767;
}

export function inCart(currentPath) {
  return (
    currentPath === '/kosik' ||
    currentPath === '/doprava-a-platba' ||
    currentPath === '/udaje-pre-dorucenie'
  );
}

export function inAccount(currentPath) {
  return currentPath.startsWith('/my-account');
}

export function inLoginOrRegister(currentPath) {
  return [
    URL_REGISTRATION_RETAIL,
    URL_REGISTRATION_COMPANY,
    URL_REGISTRATION_SCHOOL,
    URL_REGISTRATION_COMPLETE_RETAIL,
    URL_REGISTRATION_COMPLETE_COMPANY,
    URL_REGISTRATION_COMPLETE_SCHOOL,
    URL_LOGIN,
  ].includes(currentPath);
}

export function isMainMenuVisible(currentPath, currentUniqs) {
  if (inLoginOrRegister(currentPath)) {
    return false;
  }

  if (inAccount(currentPath)) {
    return false;
  }

  return (
    !currentUniqs ||
    !currentUniqs.length ||
    ![
      'SHOP_NETWORK',
      'ORGANIZATIONS',
      'HELP_AND_SUPPORT',
      'CONTACT',
      'MY_ACCOUNT',
    ].includes(currentUniqs[0])
  );
}

export function inForSchools(
  currentPath,
  currentUniqs: string[],
  cmsTree: any = null,
  singleSchoolArticle: any,
) {
  if (currentUniqs && currentUniqs.length && currentUniqs.includes('SCHOOLS')) {
    return true;
  }

  if (
    `/${singleSchoolArticle?.url}` === currentPath ||
    singleSchoolArticle?.url === currentPath
  ) {
    return true;
  }

  if (cmsTree) {
    for (const item of cmsTree) {
      if (item.children && item.children.length) {
        for (const children of item.children) {
          if (
            currentPath === getUrl(children.link) ||
            currentPath === getUrl(children.url)
          ) {
            return true;
          }
        }
      }
    }
  }

  return currentPath.startsWith(URL_FOR_SCHOOLS);
}

export function capitalizeFirstLetter(value) {
  if (!value) {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function getUrl(value) {
  if (!value || typeof value.charAt !== 'function') {
    return value;
  }
  return value.charAt(0) === '/' ? value : `/${value}`;
}

export function getSchoolName(school): string {
  if (!school) {
    return '';
  }
  return `${
    school.school_name
      ? school.school_name
      : school.name.replace(new RegExp('Základná škola', 'g'), 'ZŠ')
  }${school.street ? ', ' + school.street : ''} ${
    school.street_number ? school.street_number : ''
  }`;
}

export function getSchoolNameAndStreetFromSkvelkoSet(skvelkoSet): string {
  if (!skvelkoSet) {
    return '';
  }

  return `${
    skvelkoSet.schoolName
      ? skvelkoSet.schoolName.replace(new RegExp('Základná škola', 'g'), 'ZŠ')
      : ''
  } ${skvelkoSet.schoolStreet ? ', ' + skvelkoSet.schoolStreet : ''} ${
    skvelkoSet.schoolStreetNumber ? skvelkoSet.schoolStreetNumber : ''
  } ${skvelkoSet.city ? ', ' + skvelkoSet.city : ''}
  `;
}

export function getSchoolNameFromSkvelkoSet(skvelkoSet): string {
  if (!skvelkoSet) {
    return '';
  }

  return `${
    skvelkoSet.schoolName
      ? skvelkoSet.schoolName.replace(new RegExp('Základná škola', 'g'), 'ZŠ')
      : ''
  }
  `;
}

export function downloadBase64(
  base64: string,
  fileExtension: string,
  fileName: string = Math.random().toString(36).substr(2, 5),
): void {
  const a = document.createElement('a');
  a.href = 'data:application/octet-stream;base64,' + base64;
  a.target = '_blank';
  a.download = `${fileName}.${fileExtension}`;
  a.click();
}

export function sanitizeFileName(filename: string): string {
  return filename.replace(/[^a-z0-9]/gi, '_').toLowerCase();
}

export function getPaymentDescription(payment: any): string | undefined {
  const translation = (payment?.translations || []).find(
    (t) => t?.lang_id === 'sk',
  );
  return translation?.payment_descr;
}

export function getSetUrl(setId) {
  if (setId) {
    return `${URL_SET}/${setId}`;
  } else {
    return URL_SETS;
  }
}

export function normalizeString(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export const scrollToTop = () => {
  if (!isSsr() && typeof window.scrollTo === 'function') {
    try {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    } catch (e) {}
  }
};

export const scrollToTopTimeout = (timeout: number) => {
  setTimeout(() => scrollToTop(), timeout);
};

export const scrollToError = () => {
  if (!isSsr()) {
    setTimeout(() => {
      const el = document.querySelector('.error');
      if (el && window && typeof window.scrollTo === 'function') {
        try {
          window.scrollTo({
            top: offset(el).top - 250,
            left: 0,
            behavior: 'smooth',
          });
        } catch (e) {}
      }
    }, 200);
  }
};

export const scrollToClass = (className) => {
  if (window && document) {
    setTimeout(() => {
      const el = document.querySelector(className);
      if (el && window && typeof window.scrollTo === 'function') {
        try {
          window.scrollTo({
            top: offset(el).top - 250,
            left: 0,
            behavior: 'smooth',
          });
        } catch (e) {}
      }
    }, 200);
  }
};

const offset = (el) => {
  const rect = el.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
};

export function inSimpleTemplate(path) {
  return (
    SIMPLE_TEMPLATE_URLS.includes(path) ||
    path.includes('/survey/') ||
    path.includes('/unsubscribe/')
  );
}

export function countFinalPriceOfProducts(products) {
  let finalPrice = 0;

  if (!products) {
    return finalPrice;
  }

  for (const item of products) {
    const count: number = parseInt(item.count, 10);
    if (!isNaN(count)) {
      finalPrice += count * item.product.main_good.unitprice;
    }
  }
  return finalPrice;
}

export function isCustomerWarehouse(user: any): boolean {
  return isWarehouseType(user) || isSchool(user) || isTeacher(user);
}

export function isWarehouseType(user: any): boolean {
  return (
    user?.type_sale_price === ChematCustomerTypeSalePrice.PC1 ||
    user?.type_sale_price === ChematCustomerTypeSalePrice.PC2
  );
}

export const isRetail = (user) => {
  return user?.skvelko_type === ChematCustomerType.RETAIL;
};

export function isCompany(user: any): boolean {
  return user?.skvelko_type === ChematCustomerType.COMPANY;
}

export function isCompanyWarehouseType(user: any): boolean {
  return isCompany(user) && isWarehouseType(user);
}

export function isCompanyOrSchoolAndWarehouseType(user: any): boolean {
  return isCompanyOrSchool(user) && isWarehouseType(user);
}

export function isCompanyOrSchoolOrTeacherAndWarehouseType(user: any): boolean {
  return isCompanyOrSchoolOrTeacher(user) && isWarehouseType(user);
}

export function isTypeSalePricePC1OrPC2(user: any): boolean {
  return (
    user?.type_sale_price === ChematCustomerTypeSalePrice.PC1 ||
    user?.type_sale_price === ChematCustomerTypeSalePrice.PC2 ||
    isSchool(user) ||
    isTeacher(user)
  );
}

export function isSchoolOrTeacherAndTypeSalePricePC1OrPc2(user: any): boolean {
  return isSchoolOrTeacher(user) && isTypeSalePricePC1OrPC2(user);
}

export function isSchool(user: any): boolean {
  if (user?.skvelko_type === ChematCustomerType.SCHOOL) {
    return true;
  }
  return false;
}

export function isTeacher(user: any): boolean {
  return (
    user?.skvelko_type === ChematCustomerType.SCHOOL_PERSON &&
    user?.parent_skvelko_type === ChematCustomerType.SCHOOL
  );
}

export function isChildCompany(user: any): boolean {
  return (
    user?.skvelko_type === ChematCustomerType.SCHOOL_PERSON &&
    user?.parent_skvelko_type === ChematCustomerType.COMPANY
  );
}

export function isCompanyOrSchool(user: any): boolean {
  return isCompany(user) || isSchool(user);
}

export function isCompanyOrSchoolOrTeacher(user: any): boolean {
  return isCompany(user) || isSchool(user) || isTeacher(user);
}

export function isSchoolOrTeacher(user: any): boolean {
  return isSchool(user) || isTeacher(user);
}

export function isCompanyOrChildCompany(user: any): boolean {
  return isCompany(user) || isChildCompany(user);
}

export function isSchoolOrTeacherOrPC2Company(user: any): boolean {
  return (
    isSchool(user) ||
    isTeacher(user) ||
    (isCompany(user) &&
      user.type_sale_price === ChematCustomerTypeSalePrice.PC2)
  );
}

export function canShowHomepageSkvelkoSearch(user: any): boolean {
  return !user || isRetail(user);
}

export function canShowSkvelkoSearch(user: any): boolean {
  return !isSchoolOrTeacherOrPC2Company(user);
}

export function isRetailType(type: any): boolean {
  return type === ChematCustomerTypeSalePrice.PC3;
}

export function getUrlParts(url: string) {
  const part = url.split('/');
  let tmpEntryPath = '';
  return part
    .filter((p) => p !== '')
    .map((p) => {
      tmpEntryPath = `${tmpEntryPath}/${p}`;
      return tmpEntryPath;
    });
}

export function removeHtmlTags(text: string): string {
  return text.replace(/<\/?[^>]+(>|$)/g, '');
}

export function debounce(callback: any, wait: number, immediate = false): any {
  let timeout;
  return [
    (...args) => {
      const callNow = immediate && !timeout;
      const next = () => callback(...args);
      clearTimeout(timeout);
      timeout = setTimeout(next, wait);
      if (callNow) {
        return next();
      }
    },
    () => {
      clearTimeout(timeout);
    },
  ];
}

export function debouncePromise(callback: any, wait) {
  let timer: any;
  let resPromise: any;
  let resolver: any;
  let rejector: any;
  return (...args) => {
    if (timer) {
      // Cancel previous call
      clearTimeout(timer);
    }

    // Use native Promise as a defer object
    resPromise =
      resPromise ||
      new Promise((resolverExecutor, rejectorExecutor) => {
        resolver = resolverExecutor;
        rejector = rejectorExecutor;
      });

    timer = setTimeout(() => {
      const invokingTimer = timer;
      // There is a case that when the return value of the function is a promise,
      // we should resolve to the last invoking one instead
      const shouldProceed = () => invokingTimer === timer;
      // In case callback returns a Promise,
      // we wrap it with resolve to make it compatible with a normal function return value
      const callbackPromise = () => {
        try {
          return Promise.resolve(callback(args));
        } catch (err) {
          return Promise.reject(err);
        }
      };
      callbackPromise()
        .then(
          (res) => {
            if (shouldProceed()) {
              resolver(res);
            }
          },
          (err) => {
            if (shouldProceed()) {
              rejector(err);
            }
          },
        )
        .then(() => {
          if (shouldProceed()) {
            // Release resources
            resPromise = undefined;
            resolver = undefined;
            rejector = undefined;
            timer = undefined;
          }
        });
    }, wait);
    return resPromise;
  };
}

export function range(start: number, end: number): number[] {
  return Array.apply(null, Array(end - start + 1)).map((_, n) => n + start);
}

export function currentYear(): number {
  return new Date().getFullYear();
}

export function formatBirthDay(day, month, year): string | undefined {
  return day && month && year
    ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
        2,
        '0',
      )}`
    : undefined;
}

export function isStringNumber(str: string): boolean {
  return str === '' || /^\d+$/.test(str);
}

// ===============================
// https://github.com/hughsk/flat/blob/master/index.js
function isBuffer(obj) {
  return (
    obj &&
    obj.constructor &&
    typeof obj.constructor.isBuffer === 'function' &&
    obj.constructor.isBuffer(obj)
  );
}

function keyIdentity(key) {
  return key;
}

export function flatten(target, opts?) {
  opts = opts || {};

  const delimiter = opts.delimiter || '.';
  const maxDepth = opts.maxDepth;
  const transformKey = opts.transformKey || keyIdentity;
  const output = {};

  function step(object, prev?, currentDepth?) {
    currentDepth = currentDepth || 1;
    Object.keys(object).forEach((key) => {
      const value = object[key];
      const isarray = opts.safe && Array.isArray(value);
      const type = Object.prototype.toString.call(value);
      const isbuffer = isBuffer(value);
      const isobject = type === '[object Object]' || type === '[object Array]';

      const newKey = prev
        ? prev + delimiter + transformKey(key)
        : transformKey(key);

      if (
        !isarray &&
        !isbuffer &&
        isobject &&
        Object.keys(value).length &&
        (!opts.maxDepth || currentDepth < maxDepth)
      ) {
        return step(value, newKey, currentDepth + 1);
      }

      output[newKey] = value;
    });
  }

  step(target);

  return output;
}
// ===============================

export function hasProductCustomerPrice(
  product: any,
  goodAttrName: string = 'main_good',
): boolean {
  const customerPrices = prop(product, `${goodAttrName}.customer_prices`, []);
  return customerPrices && customerPrices.length;
}

export function getTotal(data: any): number {
  return Math.ceil(prop(data, 'total') / prop(data, 'limit'));
}

export const round = (num, precision) => {
  const factor = 10 ** precision;
  const tempNumber = num * factor;
  const roundedTempNumber = Math.round(tempNumber);
  return roundedTempNumber / factor;
};

export const loadOtherText = (
  textId,
  setOtherText,
  setLoader: (value: boolean) => void = () => {},
) => {
  return async (dispatch, getState, API) => {
    setLoader(true);

    let loadOtherTextsResult;

    try {
      loadOtherTextsResult = await API.loadOtherTexts(textId);
    } catch (e) {
      console.error(e);
    }

    setLoader(false);

    if (!loadOtherTextsResult || loadOtherTextsResult?.content?.draft === 1) {
      return;
    }

    const body = prop(loadOtherTextsResult, 'content.json_content.body', '');
    const name = prop(loadOtherTextsResult, 'content.json_content.name', '');
    if (body) {
      dispatch(setOtherText({ body, name }));
    }
  };
};

export const loadGlobalMetaTags = (sitemapUid) => {
  return async (dispatch, getState, API) => {
    const {
      general: { globalMetaTags },
    } = getState();
    if (!globalMetaTags[sitemapUid]) {
      const sitemap = await API.loadSitemap(sitemapUid);
      globalMetaTags[sitemapUid] = sitemap?.content?.json_content?.meta;
      dispatch(setGlobalMetaTags(globalMetaTags));
    }
  };
};

/**
 * Remove falsy values from an object.
 *
 * Inspired by:
 * https://stackoverflow.com/a/57625661
 *
 * @param something
 */
export const removeFalsyValues = (something: object) => {
  return Object.entries(something).reduce(
    (a, [k, v]) => (v ? ((a[k] = v), a) : a),
    {},
  );
};

export const resetForm = (values, form) => {
  // solution to reset react form shit
  // https://github.com/final-form/react-final-form/issues/21#issuecomment-604005303
  setTimeout(() => {
    Object.keys(values).forEach((key) => {
      form.resetFieldState(key);
    });
    form.reset();
  });
};

export const isOutOfStock = (product): boolean => {
  return (
    product?.isactive === 0 ||
    product?.main_good?.availability_id === 2 ||
    product?.main_good?.availability_id == null
  );
};

export const getGiftCartRemain = (cart): number => {
  return 40 - (cart?.total_items_price_with_discount || 0);
};

export const canShowProtectionModal = (
  cart,
  user,
  protectionSettings,
  isAnyModalVisible,
): boolean => {
  const nonSkvelkoSetItemsLength = cart?.items
    ? cart?.items.filter((item) => !item.skvelko_set).length
    : 0;
  const skvelkoSetsIds = cart?.items
    ? cart.items
        .map((item) => (item.skvelko_set ? item.skvelko_set.id : null))
        .filter((o) => o)
    : [];
  const skvelkoSetLength = skvelkoSetsIds.filter(
    (item, index) => skvelkoSetsIds.indexOf(item) === index,
  ).length;

  return (
    !cookie.load(CART_PROTECTION_COOKIE) &&
    !isAnyModalVisible &&
    !user &&
    !cart?.protection_modal_was_displayed &&
    (cart?.billing_address?.person?.email === '' ||
      cart?.billing_address?.person?.phone === '') &&
    cart?.total_price >= protectionSettings?.price &&
    nonSkvelkoSetItemsLength + skvelkoSetLength > protectionSettings?.count
  );
};

const getDiscountPercentageFromFromCart = (cart) => {
  if (cart?.discount_type === 'giftcard') {
    const [giftcard] = cart?.giftcards?.length ? cart.giftcards : [];
    return giftcard?.discount;
  } else if (cart?.discount_type === 'customer_card') {
    return cart.customer_card_discount;
  } else {
    return null;
  }
};

export const getCustomerPrice = (good: any, user: any) => {
  const userId = user?.id;
  const customerPrices = good?.customer_prices;

  if (!userId || !customerPrices || !customerPrices.length) {
    return undefined;
  }

  const customerPrice = customerPrices.find(
    (c) => c?.user_id === userId || c?.userId === userId,
  );

  if (!customerPrice) {
    return undefined;
  }

  return customerPrice?.unitprice || customerPrice?.unitPrice;
};

export const getDiscountPrice = (
  cart,
  good,
  user: any = null,
  showWithVat = true,
) => {
  if (!good?.final_price) {
    return null;
  }

  let discount = getDiscountPercentageFromFromCart(cart);
  if (!discount) {
    return null;
  }

  const price = round(good?.final_price * ((100 - discount) / 100), 2);
  const priceWithoutVat = getPriceWithoutVat(price, good?.vat_rate);

  return showWithVat ? price : priceWithoutVat;
};

export const showPriceWithVat = (user) =>
  !user || !isCompanyOrChildCompany(user);

export const isDiscountInCart = (cart, user: any = null) =>
  !!cart?.is_giftcards_valid ||
  !!cart?.customer_card_discount ||
  !!user?.customer_card_discount;

export const getPriceWithoutVat = (price, vatRate) =>
  round(price / (1 + vatRate / 100), 2);

export const getProductPrices = (
  product: any,
  good: any,
  cart: any,
  user: any,
  count: number = 1,
) => {
  const retailGroupPrice = good?.group_prices?.find((g) =>
    isRetailType(g?.skupina_id),
  );
  let customerPrice = getCustomerPrice(good, user);

  const showWithVat = showPriceWithVat(user);
  const discountPrice =
    (getDiscountPrice(cart, good, user, showWithVat) || 0) * count;
  const vatRate = good?.vat_rate;

  let unitPrice = showWithVat
    ? good?.final_price
    : good?.final_price_without_vat;
  if (customerPrice) {
    unitPrice = showWithVat
      ? customerPrice
      : getPriceWithoutVat(customerPrice, vatRate);
  }
  unitPrice = round(unitPrice, 2);
  const price = unitPrice * count;

  const priceWithVat =
    (customerPrice ? customerPrice : good?.final_price) * count;

  const priceWithoutVat = customerPrice
    ? getPriceWithoutVat(customerPrice, vatRate)
    : good?.final_price_without_vat;

  let oldPrice = showWithVat
    ? good?.oldprice
    : getPriceWithoutVat(good?.oldprice || 0, vatRate);
  oldPrice = round(oldPrice, 2) * count;

  let retailPrice = retailGroupPrice?.oldprice || retailGroupPrice?.unitprice;
  if (!showWithVat) {
    retailPrice = getPriceWithoutVat(retailPrice, vatRate);
  }
  retailPrice = round(retailPrice, 2) * count;

  if (good?.price_create_type === 1) {
    customerPrice = good.price_without_vat;
  }

  return {
    currencyId: good?.currency_id || 'EUR',
    unitPrice,
    price,
    priceWithVat,
    priceWithoutVat,
    retailPrice,
    oldPrice,
    oldPriceWithoutVat: getPriceWithoutVat(oldPrice, vatRate),
    discountPrice,
    discountPriceWithoutVat: getPriceWithoutVat(discountPrice, vatRate),
    retailGroupPrice,
    isSale: !!good?.oldprice,
    isDiscount: !!(isDiscountInCart(cart, user) && discountPrice),
    isCustomerPrice: !!customerPrice,
    isWarehouse: isCustomerWarehouse(user),
    showWithVat,
  };
};

export const isOrderPaid = (order): boolean => {
  const paymentType = order?.payment?.payment_type;
  return (
    order?.order_paid_datetime !== null ||
    isDobierka(paymentType) ||
    isVyzvaNaPrevod(paymentType) ||
    isFaktura(paymentType) ||
    isZbernaFaktura(paymentType)
  );
};

export const changeQueryParameters = (dispatch, query) => {
  if (!isSsr()) {
    const location =
      window.location.pathname + '?' + new URLSearchParams(query).toString();
    dispatch(push(location));
  }
};

export const isObjectEmpty = (object) =>
  object && Object.keys(object).length === 0 && object.constructor === Object;

export const isSameObject = (object1, object2) =>
  JSON.stringify(object1) === JSON.stringify(object2);

export const cookieAgree = () => {
  cookie.save('COOKIES', null, { expires: cookiesExpiresDate(), path: '/' });
  cookie.save('COOKIES_REJECT', 0, {
    expires: cookiesExpiresDate(),
    path: '/',
  });
};

export const cookieDisagree = () => {
  cookie.save('COOKIES', null, { expires: cookiesExpiresDate(), path: '/' });
  cookie.save('COOKIES_REJECT', 1, {
    expires: cookiesExpiresDate(),
    path: '/',
  });
};

export function parseShortcode(shortcode) {
  var attributes = {};
  shortcode.match(/[\w-\[\]]+=".*?"/g)?.forEach(function (attribute) {
    attribute = attribute.match(/([\w-\[\]]+)="(.+?)"/);
    attributes[attribute[1]] = attribute[2];
  });

  shortcode.match(/[\w-\[\]<>{}]+='.*?'/g)?.forEach(function (attribute) {
    attribute = attribute.match(/([\w-\[\]<>{}]+)='(.+?)'/);
    attributes[attribute[1]] = attribute[2];
  });

  return attributes;
}

export function parseTextWithShortcode(
  text,
): Array<{
  type: 'string';
  parameters?: Array<any>;
  value?: any;
}> {
  const parts: Array<any> = [];
  const textParts: Array<string> = [];

  const shortSplits: Array<string> = text
    .replace(/(\r\n|\n|\r)/gm, '')
    .split(/(?=\[flipbook|\[3dmodel|\[formular-sutaz)/g);

  for (const shortSplit of shortSplits) {
    if (
      shortSplit.startsWith('[flipbook') ||
      shortSplit.startsWith('[3dmodel') ||
      shortSplit.startsWith('[formular-sutaz')
    ) {
      const index = shortSplit.indexOf(']');
      textParts.push(shortSplit.substring(0, index + 1));
      textParts.push(shortSplit.substring(index + 1));
    } else {
      textParts.push(shortSplit);
    }
  }

  for (var textPart of textParts) {
    if (textPart.startsWith('[flipbook')) {
      parts.push({
        type: 'flipbook',
        parameters: parseShortcode(textPart),
      });
    } else if (textPart.startsWith('[3dmodel')) {
      parts.push({
        type: '3dmodel',
        parameters: parseShortcode(textPart),
      });
    } else if (textPart.startsWith('[formular-sutaz')) {
      parts.push({
        type: 'formular-sutaz',
        parameters: parseShortcode(textPart),
      });
    } else {
      parts.push({
        type: 'text',
        value: textPart,
      });
    }
  }

  return parts;
}

export const toArray = (value: any): any[] => {
  if (Array.isArray(value)) {
    return value;
  } else {
    return [value];
  }
};

export const getSkvelkoSetName = (name1, name2, name3) => {
  let name = '';
  if (name1) {
    name += name1;
  }

  if (name2) {
    if (name1) {
      name += ' ' + name2;
    } else {
      name += name2;
    }
  }

  if (name3) {
    if (name1 || name2) {
      name += ' - ' + name3;
    } else {
      name += name3;
    }
  }
  return 'Zoznam pre ' + name;
};

export const getFileLink = (contentId: number, fileName: string) => {
  return `${process.env.REACT_APP_API_BASE_URL}/file?query=media/${contentId}/${fileName}`;
};

export const text2ascii = (text: string) => {
  return text
    .replace(/[áàãâäă]/gi, 'a')
    .replace(/[čç]/gi, 'c')
    .replace(/[ď]/gi, 'd')
    .replace(/[éèê]/gi, 'e')
    .replace(/[íìïî]/gi, 'i')
    .replace(/[ľĺ]/gi, 'l')
    .replace(/[ñň]/gi, 'n')
    .replace(/[óòöôõ]/gi, 'o')
    .replace(/[ř]/gi, 'r')
    .replace(/[šșŞş]/gi, 's')
    .replace(/[ťţ]/gi, 't')
    .replace(/[úùüûů]/gi, 'u')
    .replace(/[ý]/gi, 'y')
    .replace(/[ž]/gi, 'z')
    .replace(/([^\x00-\xFF]|\s)*$/g, '')
    .replace(/[^\x20-\x7E]/g, '');
};

export const removeDiacriticsToLower = (text: string) => {
  return text2ascii(text).toLowerCase();
};

export const isCartGiftsValid = (
  settings: {
    cartActionFrom;
    cartActionTo;
  },
  user: any = null,
): boolean => {
  if (!settings) {
    return false;
  }

  if (user && !isRetail(user)) {
    return false;
  }

  return dayjs().isBetween(
    settings.cartActionFrom,
    dayjs(settings.cartActionTo),
  );
};

export const getGiftCategoryDesktopBannerFromSettings = (settings) => {
  return settings.cartActionCategoryDesktopBanner;
};

export const getGiftSetsDesktopBannerFromSettings = (settings) => {
  return settings.cartActionSetsDesktopBanner;
};

export const getGiftWebContentIdFromSettings = (settings) => {
  return settings.cartActionModalWebContentId;
};

export const combineWords = (valuesArray: String[]) => {
  return getCombinations(valuesArray).map((a) => a.join(' '));
};

export const getCombinations = (valuesArray: String[]) => {
  var combi: Array<any> = [];
  var temp: Array<any> = [];
  var slent = Math.pow(2, valuesArray.length);

  for (var i = 0; i < slent; i++) {
    temp = [];
    for (var j = 0; j < valuesArray.length; j++) {
      if (i & Math.pow(2, j)) {
        temp.push(valuesArray[j]);
      }
    }
    if (temp.length > 0) {
      combi.push(temp);
    }
  }

  combi.sort((a, b) => a.length - b.length);
  return combi;
};

export const canShowCustomerProducts = (user) => {
  return (
    user &&
    (isCompanyOrChildCompany(user) ||
      (isSchoolOrTeacherAndTypeSalePricePC1OrPc2(user) &&
        user.has_customer_products))
  );
};

export const splitStreetWithNumber = (
  streetString: string,
): { street: string; number: string } => {
  const rx = /^(.*?)\s+(\d+(?:[-.\/]\d+)?)(?:\s*(\S.*))?$/;
  const resp = streetString.match(rx);

  if (resp) {
    return {
      street: resp[1],
      number: resp[2],
    };
  }

  return {
    street: '',
    number: '',
  };
};

export const filterBannersByUser = (banners, user) => {
  if (banners && banners.length) {
    return banners.filter((banner) => {
      const visibleForArra: Array<string> = banner.visible_for
        ? banner.visible_for.split(' ')
        : [];
      if (!visibleForArra.length) {
        return false;
      }

      if (!user && visibleForArra.includes('NOT_LOGGED')) {
        return true;
      }

      if (user) {
        if (user.skvelko_type == 1 && visibleForArra.includes('RETAIL'))
          return true;
        if (user.skvelko_type == 2 && visibleForArra.includes('COMPANY'))
          return true;
        if (user.skvelko_type == 3 && visibleForArra.includes('SCHOOL'))
          return true;
        if (user.skvelko_type == 4) {
          if (
            user.parent_skvelko_type == 2 &&
            visibleForArra.includes('COMPANY_CHILD')
          )
            return true;
          if (
            user.parent_skvelko_type == 3 &&
            visibleForArra.includes('SCHOOL_CHILD')
          )
            return true;
        }
      }

      return false;
    });
  }

  return banners;
};

export const removePTag = (text: string): string => {
  return text.replace(/^<p>/g, '').replace(/<\/p>$/g, '');
};

export const objectsEqual = (o1, o2) =>
  Object.keys(o1).length === Object.keys(o2).length &&
  Object.keys(o1).every((p) => o1[p] === o2[p]);

export const objectsArraysEqual = (a1, a2) =>
  a1.length === a2.length && a1.every((o, idx) => objectsEqual(o, a2[idx]));

export const getCartRegularItems = (cart) => {
  const regularCartItems: any[] = [];
  if (cart && cart.items?.length > 0) {
    cart.items.forEach((cartItem) => {
      if (!cartItem.product?.on_demand) {
        regularCartItems.push(cartItem);
      }
    });
  }
  return regularCartItems;
};

export const getCartOnDemandItems = (cart) => {
  const onDemandCartItems: any[] = [];
  if (cart && cart.items?.length > 0) {
    cart.items.forEach((cartItem) => {
      if (cartItem.product?.on_demand) {
        onDemandCartItems.push(cartItem);
      }
    });
  }
  return onDemandCartItems;
};

export const isCartOnlyDemand = (cart) => {
  let onDemandCartItems: any[] = [];
  let regularCartItems: any[] = [];

  if (cart && cart.items?.length > 0) {
    cart.items.forEach((cartItem) => {
      if (cartItem.product?.on_demand) {
        onDemandCartItems.push(cartItem);
      } else {
        regularCartItems.push(cartItem);
      }
    });

    if (regularCartItems.length === 0 && onDemandCartItems.length > 0) {
      return true;
    }
  }

  return false;
};

export function removeObjectFromArray(arr, propertyName, propertyValue) {
  for (var i = 0; i < arr.length; i++) {
    if (arr[i][propertyName] === propertyValue) {
      arr.splice(i, 1);
      return; // Exit the function after removal
    }
  }
}

export function itemIsSkvelko(item) {
  if (
    item?.product_plu?.startsWith('S') ||
    item?.plu?.startsWith('S') ||
    item?.productDetail?.product_plu?.startsWith('S') ||
    item?.main_good?.product_plu?.startsWith('S') ||
    item?.product?.plu?.startsWith('S') ||
    item?.product?.product_plu?.startsWith('S')
  ) {
    return true;
  }

  return false;
}

export function itemIsSkvelkoAndGoodPriceEnabled(item, settings) {
  if (itemIsSkvelko(item) && !!settings?.eshop_actionTypeGoodPrice) {
    return true;
  }

  return false;
}

export function getStickedSaleText(product: any) {
  if (itemIsSkvelko(product)) {
    return __('DOBRÁ CENA');
  }

  return __('AKCIA');
}

export function getAgeByDateString(dateString) {
  var today = new Date();
  var birthDate = new Date(dateString);
  var age = today.getFullYear() - birthDate.getFullYear();
  var m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

export function loadSrrCookieOrDefault(ssrCookies, cookieName) {
  if (isSsr() && typeof ssrCookies === 'object' && ssrCookies !== null) {
    return ssrCookies[cookieName];
  } else {
    return cookie.load(cookieName);
  }
}

export function isNonEmptyObject(obj) {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    !Array.isArray(obj) &&
    Object.keys(obj).length > 0
  );
}
